import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import type { PartnerInsightsFilters } from "../types";
import {
	DATE_PRESETS,
	type DatePreset,
	DEFAULT_PRESET,
	isIsoDate,
	presetRange,
} from "./dates";

// The dashboard's filters live in the URL, so the "View all" pages read the
// same filters as the home page and a copied link opens the same view.

export type TrendGranularity = "day" | "week" | "month";
export type CountryScope = "destination" | "source";

export interface InsightsSearch {
	preset?: DatePreset;
	from?: string;
	to?: string;
	/** Destination countries (names, as filter_options lists them). */
	dc?: string[];
	/** Destination cities. */
	city?: string[];
	/** Source countries (ISO codes, as filter_options lists them). */
	sc?: string[];
	/** Sub-partner ids (Metabase's Subpartner filter). */
	sp?: string[];
	trend?: TrendGranularity;
	/** "0" turns the previous-period comparison off; it is on by default. */
	compare?: "0";
	/** Top countries page tab. */
	scope?: CountryScope;
	/** Agent access keeps the partner in the URL (see __root). */
	partner_id?: string;
}

const MAX_VALUES = 50;

function strings(value: unknown): string[] | undefined {
	const list = Array.isArray(value) ? value : value ? [value] : [];
	const clean = list
		.filter(
			(item): item is string =>
				typeof item === "string" && item.length > 0 && item.length <= 120,
		)
		.slice(0, MAX_VALUES);
	return clean.length ? clean : undefined;
}

/** Positive ids, which the router may parse from the URL as numbers or strings. */
function ids(value: unknown): string[] | undefined {
	const list = Array.isArray(value) ? value : value ? [value] : [];
	const clean = list
		.map((item) => String(item))
		.filter((item) => /^[1-9]\d{0,11}$/.test(item))
		.slice(0, MAX_VALUES);
	return clean.length ? [...new Set(clean)] : undefined;
}

/** validateSearch for the dashboard and its "View all" routes. */
export function validateInsightsSearch(
	search: Record<string, unknown>,
): InsightsSearch {
	const preset = DATE_PRESETS.includes(search.preset as DatePreset)
		? (search.preset as DatePreset)
		: undefined;
	const custom = preset === "custom";
	return {
		preset,
		from: custom && isIsoDate(search.from) ? search.from : undefined,
		to: custom && isIsoDate(search.to) ? search.to : undefined,
		dc: strings(search.dc),
		city: strings(search.city),
		sc: strings(search.sc),
		sp: ids(search.sp),
		trend: ["day", "week", "month"].includes(search.trend as string)
			? (search.trend as TrendGranularity)
			: undefined,
		compare: search.compare === "0" || search.compare === 0 ? "0" : undefined,
		scope: search.scope === "source" ? "source" : undefined,
		partner_id:
			search.partner_id !== undefined && search.partner_id !== null
				? String(search.partner_id)
				: undefined,
	};
}

export interface InsightsState {
	preset: DatePreset;
	filters: PartnerInsightsFilters;
	/** On only when there is a date range to compare against. */
	compare: boolean;
	/** What the toggle shows: the viewer's choice, whatever the range. */
	compareChosen: boolean;
	scope: CountryScope;
	/** Destination country, destination city, source country and sub-partner values set. */
	dimensionCount: number;
	update: (patch: Partial<InsightsSearch>) => void;
	setPreset: (preset: DatePreset, range?: { from: string; to: string }) => void;
	clearDimensions: () => void;
}

export function useInsightsState(): InsightsState {
	const search = useSearch({ strict: false }) as InsightsSearch;
	const navigate = useNavigate();

	const preset = search.preset ?? DEFAULT_PRESET;
	const range =
		preset === "custom"
			? search.from && search.to
				? { from: search.from, to: search.to }
				: null
			: presetRange(preset);

	const filters = useMemo<PartnerInsightsFilters>(
		() => ({
			date_from: range?.from ?? null,
			date_to: range?.to ?? null,
			destination_country: search.dc ?? [],
			destination_city: search.city ?? [],
			source_country: search.sc ?? [],
			subpartner_id: search.sp ?? [],
			trend_type: search.trend ?? "month",
		}),
		[
			range?.from,
			range?.to,
			search.dc,
			search.city,
			search.sc,
			search.sp,
			search.trend,
		],
	);

	const update = useCallback(
		(patch: Partial<InsightsSearch>) => {
			navigate({
				to: ".",
				search: (prev: Record<string, unknown>) => {
					const next: Record<string, unknown> = { ...prev, ...patch };
					for (const key of Object.keys(next)) {
						const value = next[key];
						if (
							value === undefined ||
							(Array.isArray(value) && value.length === 0)
						) {
							delete next[key];
						}
					}
					return next;
				},
				replace: true,
			});
		},
		[navigate],
	);

	const setPreset = useCallback(
		(next: DatePreset, custom?: { from: string; to: string }) => {
			update({
				preset: next === DEFAULT_PRESET ? undefined : next,
				from: next === "custom" ? custom?.from : undefined,
				to: next === "custom" ? custom?.to : undefined,
			});
		},
		[update],
	);

	const clearDimensions = useCallback(
		() =>
			update({ dc: undefined, city: undefined, sc: undefined, sp: undefined }),
		[update],
	);

	const compareChosen = search.compare !== "0";
	return {
		preset,
		filters,
		compare: compareChosen && !!range,
		compareChosen,
		scope: search.scope ?? "destination",
		dimensionCount:
			filters.destination_country.length +
			filters.destination_city.length +
			filters.source_country.length +
			filters.subpartner_id.length,
		update,
		setPreset,
		clearDimensions,
	};
}
